import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const MobileCart = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const kt = searchParams.get("kt") || "";

  const [loading, setLoading] = useState(true);
  const [shopName, setShopName] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const formatPrice = useCallback((value) => {
    return `${Number(value || 0).toLocaleString("ko-KR")}원`;
  }, []);

  const getImageSrc = useCallback((imagePath) => {
    if (!imagePath) return "";
    if (
      imagePath.startsWith("http://") ||
      imagePath.startsWith("https://")
    ) {
      return imagePath;
    }
    if (imagePath.startsWith("/static/")) return imagePath;
    if (imagePath.startsWith("static/")) return `/${imagePath}`;
    if (imagePath.startsWith("/")) return `/static${imagePath}`;
    return `/static/${imagePath}`;
  }, []);

  const fetchCart = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get("/api/kiosk/mobile_cart", {
        withCredentials: true,
      });

      const data = res.data || {};

      if (!data.ok) {
        alert(data.message || "세션이 없습니다. QR을 다시 찍어주세요.");
        setItems([]);
        setTotal(0);
        setShopName("");
        return;
      }

      setShopName(data.shop_name || "");
      setItems(Array.isArray(data.items) ? data.items : []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      console.error("모바일 장바구니 조회 실패:", err);
      alert(
        err.response?.data?.message ||
          "장바구니를 불러오지 못했습니다."
      );
      setItems([]);
      setTotal(0);
      setShopName("");
    } finally {
      setLoading(false);
    }
  }, []);

  const submitAction = useCallback(
    async (payload) => {
      try {
        const res = await axios.post(
          "/api/kiosk/mobile_cart",
          payload,
          {
            withCredentials: true,
          }
        );

        const data = res.data || {};

        if (!data.ok) {
          alert(data.message || "처리에 실패했습니다.");
          return false;
        }

        await fetchCart();
        return true;
      } catch (err) {
        console.error("모바일 장바구니 처리 실패:", err);
        alert(
          err.response?.data?.message ||
            "처리에 실패했습니다."
        );
        return false;
      }
    },
    [fetchCart]
  );

  const changeQty = useCallback(
    async (cartId, productId, quantity) => {
      await submitAction({
        action: "update_qty",
        cart_id: cartId,
        product_id: productId,
        quantity,
      });
    },
    [submitAction]
  );

  const removeItem = useCallback(
    async (cartId, productId) => {
      if (!window.confirm("삭제할까요?")) return;

      await submitAction({
        action: "remove",
        cart_id: cartId,
        product_id: productId,
      });
    },
    [submitAction]
  );

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  return (
    <>
      <style>{`
        body {
          background: #f6f7f9;
        }

        .card {
          border: 0;
          border-radius: 14px;
        }

        .thumb {
          width: 72px;
          height: 72px;
          object-fit: cover;
          border-radius: 10px;
          background: #eee;
          flex: 0 0 72px;
        }

        .sticky-bar {
          position: sticky;
          bottom: 0;
          z-index: 10;
        }

        .qty-box {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .qty-btn {
          width: 38px;
          height: 34px;
          border: 1px solid #ddd;
          background: #f8f8f8;
          border-radius: 8px;
          font-weight: 800;
          line-height: 1;
        }

        .qty-btn:active {
          transform: scale(0.98);
        }

        .qty-num {
          min-width: 28px;
          text-align: center;
          font-weight: 800;
        }

        .delete-btn {
          background: none;
          border: none;
          color: #c7c7c7;
          font-size: 1.25rem;
          padding: 0;
          line-height: 1;
        }

        .delete-btn:hover {
          color: #dc3545;
        }

        .option-line {
          font-size: 0.82rem;
          color: #6c757d;
          line-height: 1.35;
        }

        .normal-price {
          color: #999;
          font-size: 0.82rem;
          text-decoration: line-through;
        }

        .sale-price {
          color: #dc3545;
          font-weight: 800;
          font-size: 0.9rem;
        }

        .option-total {
          color: #0d6efd;
          font-size: 0.82rem;
        }

        .unit-price {
          color: #6c757d;
          font-size: 0.82rem;
        }
      `}</style>

      <div className="container py-3">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <div className="text-muted small">{shopName}</div>
            <h4 className="fw-bold m-0">장바구니</h4>
          </div>

          <button
            type="button"
            onClick={() => window.history.back()}
            className="btn btn-outline-secondary btn-sm"
          >
            뒤로
          </button>
        </div>

        {loading ? (
          <div className="card p-3">불러오는 중...</div>
        ) : items.length === 0 ? (
          <div className="card p-3">
            <div className="fw-bold mb-1">
              장바구니가 비었습니다
            </div>
            <div className="text-muted small">
              키오스크에서 상품을 담아주세요.
            </div>
          </div>
        ) : (
          <>
            {items.map((it, idx) => {
              const options = Array.isArray(it.options)
                ? it.options
                : [];

              const originalPrice = Number(
                it.original_price ??
                  it.normal_price ??
                  it.price ??
                  0
              );

              const displayPrice = Number(
                it.display_price ??
                  it.product_price ??
                  it.price ??
                  0
              );

              const optionTotal = Number(
                it.option_total ??
                  it.option_price ??
                  0
              );

              const unitPrice = Number(
                it.unit_price ??
                  it.price ??
                  (displayPrice + optionTotal)
              );

              const quantity = Number(
                it.quantity ??
                  it.qty ??
                  1
              );

              const subtotal = Number(
                it.subtotal ??
                  it.line ??
                  it.line_total ??
                  (unitPrice * quantity)
              );

              const isSale =
                Boolean(it.is_sale) ||
                (
                  originalPrice > 0 &&
                  displayPrice > 0 &&
                  displayPrice < originalPrice
                );

              return (
                <div
                  className="card mb-2"
                  key={`${it.cart_id || it.product_id}-${idx}`}
                >
                  <div className="card-body d-flex gap-3 align-items-center">
                    {it.image_path ? (
                      <img
                        src={getImageSrc(it.image_path)}
                        className="thumb"
                        alt=""
                      />
                    ) : (
                      <div className="thumb" />
                    )}

                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="fw-bold">{it.name}</div>

                        <button
                          type="button"
                          className="delete-btn"
                          aria-label="삭제"
                          onClick={() =>
                            removeItem(
                              Number(it.cart_id || 0),
                              Number(it.product_id || 0)
                            )
                          }
                        >
                          <i className="bi bi-x-circle" />
                        </button>
                      </div>

                      {options.length > 0 ? (
                        <div className="mt-1">
                          {options.map((opt, optIdx) => {
                            const groupName =
                              opt.option_group_name ||
                              opt.group_name ||
                              "";

                            const itemName =
                              opt.option_item_name ||
                              opt.item_name ||
                              "";

                            const optionPrice = Number(
                              opt.option_price ??
                                opt.extra_price ??
                                0
                            );

                            return (
                              <div
                                className="option-line"
                                key={`${it.cart_id || it.product_id}-${optIdx}`}
                              >
                                · {groupName ? `${groupName}: ` : ""}
                                {itemName}
                                {optionPrice !== 0
                                  ? ` (${optionPrice > 0 ? "+" : ""}${formatPrice(optionPrice)})`
                                  : ""}
                              </div>
                            );
                          })}
                        </div>
                      ) : null}

                      <div className="mt-1">
                        {isSale ? (
                          <div className="normal-price">
                            정상가 {formatPrice(originalPrice)}
                          </div>
                        ) : null}

                        <div className={isSale ? "sale-price" : "text-muted small"}>
                          상품 {formatPrice(displayPrice)}
                        </div>

                        {optionTotal > 0 ? (
                          <div className="option-total">
                            옵션 +{formatPrice(optionTotal)}
                          </div>
                        ) : null}

                        <div className="unit-price">
                          단가 {formatPrice(unitPrice)}
                        </div>
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <div className="qty-box">
                          <button
                            type="button"
                            className="qty-btn"
                            disabled={quantity <= 1}
                            onClick={() =>
                              changeQty(
                                Number(it.cart_id || 0),
                                Number(it.product_id || 0),
                                Math.max(1, quantity - 1)
                              )
                            }
                          >
                            −
                          </button>

                          <div className="qty-num">
                            {quantity}
                          </div>

                          <button
                            type="button"
                            className="qty-btn"
                            onClick={() =>
                              changeQty(
                                Number(it.cart_id || 0),
                                Number(it.product_id || 0),
                                quantity + 1
                              )
                            }
                          >
                            ＋
                          </button>
                        </div>

                        <div className="fw-bold">
                          {formatPrice(subtotal)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}

            <div className="card mt-3">
              <div className="card-body d-flex justify-content-between">
                <div className="fw-bold">합계</div>
                <div className="fw-bold">
                  {formatPrice(total)}
                </div>
              </div>
            </div>

            <div className="sticky-bar mt-3">
              <button
                type="button"
                className="btn btn-primary w-100 py-3 fw-bold"
                onClick={() =>
                  navigate(
                    `/kiosk/mobile/checkout?kt=${encodeURIComponent(kt)}`
                  )
                }
              >
                결제하기 ({formatPrice(total)})
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default MobileCart;
